/**
 * Wie viele Belege ein Projekt schon hat — getrennt nach ausgestellt und noch
 * offenem Entwurf.
 *
 * WARUM getrennt: die beiden Zahlen beantworten zwei verschiedene Fragen. Die
 * ausgestellten Belege sind die Abrechnungshistorie des Projekts (steht der
 * Monat schon beim Kunden?). Ein offener Entwurf dagegen ist eine Warnung: die
 * Mengen, die der Anlagedialog gerade als „offen" anbietet, sind zwar noch
 * nicht abgerechnet, aber jemand hat sie bereits in einem Entwurf beansprucht.
 * Wer daneben einen zweiten Entwurf anlegt, hat zwei Belege ueber dieselbe
 * Leistung — beim Ausstellen faellt der zweite entweder auf 0,00 EUR oder,
 * schlimmer, er wird zuerst ausgestellt und der erste bleibt als Karteileiche
 * stehen. Der Dialog konnte das bisher nicht anzeigen.
 *
 * Bewusst eine pure Funktion in einer Servicedatei (stehende Entscheidung
 * „duenner Container, Logik in puren Funktionen"; das Projekt hat keine
 * DOM-Testumgebung, Komponenten sind also nicht testbar).
 *
 * Gerechnet wird ueber die ohnehin geladene Belegliste der Rechnungsansicht —
 * kein zusaetzlicher Abruf. Die Liste kommt ohne Positionen, was hier genuegt:
 * gezaehlt wird am Kopf des Belegs.
 */

import type { DocumentType, InvoiceStatus } from '../types';

/**
 * Zaehlt ein Beleg in DIESEM Status als offener Entwurf?
 *
 * Bewusst ein `Record` ueber den vollstaendigen Status und keine Menge: ein
 * neuer Status bricht dann den Typecheck, statt stillschweigend als „kein
 * Entwurf" durchzulaufen und die Warnung auszuschalten.
 *
 * ISSUING zaehlt mit: der Beleg ist mitten in der Ausstellung, hat aber noch
 * keine Nummer. Ihn hier auszulassen hiesse, waehrend genau dieser Sekunden
 * einen zweiten Entwurf anzubieten.
 *
 * CANCELLED ohne Nummer ist ein verworfener Entwurf und beansprucht nichts
 * mehr — er darf die Anlage nicht bremsen. Alle uebrigen Status setzen eine
 * Nummer voraus und werden deshalb ueber `invoiceNumber` erfasst, nicht hier.
 */
const COUNTS_AS_OPEN_DRAFT: Record<InvoiceStatus, boolean> = {
    DRAFT: true,
    READY_FOR_REVIEW: true,
    APPROVED: true,
    ISSUING: true,
    ISSUED: false,
    SENT: false,
    PARTIALLY_PAID: false,
    PAID: false,
    PARTIALLY_CREDITED: false,
    CREDITED: false,
    REFUND_PENDING: false,
    REFUNDED: false,
    DISPUTED: false,
    OVERDUE: false,
    SUPERSEDED_BY_FINAL_INVOICE: false,
    CANCELLED: false,
};

/** Das Wenige, was ein Beleg fuer diese Zaehlung mitbringen muss. */
export interface DocumentTallyStatement {
    id?: string | null;
    /** Einzelbeleg: gesetzt. Sammelrechnung: leer — sie zaehlt fuer kein Projekt. */
    primaryProjectId?: string | null;
    /** Erst ab dem Ausstellen vergeben. Ihr Vorhandensein IST die Ausstellung. */
    invoiceNumber?: string | null;
    status?: InvoiceStatus | null;
    documentType?: DocumentType | null;
    invoiceDate?: string | null;
}

/** Der juengste offene Entwurf eines Projekts — zum Oeffnen statt Neuanlegen. */
export interface OpenDraftRef {
    id: string;
    documentType: DocumentType | null;
}

/** Der juengste ausgestellte Beleg — Nummer und Datum fuer die Ansage. */
export interface IssuedRef {
    invoiceNumber: string;
    invoiceDate: string | null;
}

export interface ProjectDocumentTally {
    /** Ausgestellte Belege des Projekts, jede Belegart. */
    issued: number;
    /** Noch nicht ausgestellte Belege (Entwurf, Freigabe, Ausstellung laeuft). */
    drafts: number;
    /** Der juengste davon, `null` wenn es keinen gibt. */
    latestDraft: OpenDraftRef | null;
    /** Der juengste ausgestellte Beleg, `null` wenn es keinen gibt. */
    lastIssued: IssuedRef | null;
}

export const EMPTY_DOCUMENT_TALLY: ProjectDocumentTally = {
    issued: 0,
    drafts: 0,
    latestDraft: null,
    lastIssued: null,
};

/**
 * Ordnung der Belege untereinander: Datum zuerst, dann die Nummer bzw. die id
 * als Gleichstand — an einem Tag entstehen mehrere Belege, und ohne einen
 * zweiten Schluessel haengt „der juengste" an der Reihenfolge der Abfrage.
 */
const stampOf = (inv: DocumentTallyStatement): string =>
    `${inv.invoiceDate ?? ''}|${inv.invoiceNumber ?? ''}|${inv.id ?? ''}`;

/**
 * Die Belegzahlen je Projekt, aus einer Belegliste ueber mehrere Projekte.
 *
 * Belege ohne `primaryProjectId` (Sammelrechnung) zaehlen fuer kein Projekt:
 * ihre Zuordnung steht in den Zeilen, und die Liste hat keine Zeilen. Lieber
 * gar nicht gezaehlt als dem falschen Projekt zugeschrieben.
 */
export const tallyProjectDocuments = (
    invoices: readonly DocumentTallyStatement[],
): Map<string, ProjectDocumentTally> => {
    const out = new Map<string, ProjectDocumentTally>();
    // Die Stempel des jeweils juengsten Belegs je Art, getrennt gefuehrt: der
    // juengste Entwurf und der juengste ausgestellte Beleg sind verschiedene
    // Belege und duerfen sich nicht gegenseitig verdraengen.
    const newestDraft = new Map<string, string>();
    const newestIssued = new Map<string, string>();

    for (const inv of invoices) {
        const projectId = inv.primaryProjectId;
        if (!projectId) continue;
        const tally = out.get(projectId) ?? { ...EMPTY_DOCUMENT_TALLY };
        const stamp = stampOf(inv);

        if (inv.invoiceNumber) {
            tally.issued += 1;
            if ((newestIssued.get(projectId) ?? '') <= stamp) {
                newestIssued.set(projectId, stamp);
                tally.lastIssued = {
                    invoiceNumber: inv.invoiceNumber,
                    invoiceDate: inv.invoiceDate ?? null,
                };
            }
        } else if (inv.status != null && COUNTS_AS_OPEN_DRAFT[inv.status] === true) {
            tally.drafts += 1;
            // Ohne id laesst sich der Entwurf nicht oeffnen — dann bleibt es
            // bei der Zahl. Die Warnung ist auch ohne Sprungziel richtig.
            if (inv.id && (newestDraft.get(projectId) ?? '') <= stamp) {
                newestDraft.set(projectId, stamp);
                tally.latestDraft = { id: inv.id, documentType: inv.documentType ?? null };
            }
        }

        out.set(projectId, tally);
    }
    return out;
};
